"use client";

import {
  getDesktopMainWindowFocused,
  sendDesktopNotification,
} from "@/lib/desktop/notifications";
import {
  createTaskNotificationDeduper,
  defaultTaskNotificationSettings,
  readTaskNotificationSettings,
  readTrackedResearchDrawingTaskIds,
  readTrackedSummaryTasks,
  resolveSummaryTaskNotificationOutcome,
  shouldDeliverDesktopNotification,
  shouldDeliverInAppNotification,
  trackResearchDrawingTaskInStorage,
  trackSummaryTaskInStorage,
  type DesktopNotificationMode,
  type PendingSummaryTask,
  type SummaryTaskNotificationStage,
  type TaskNotificationSettings,
  untrackResearchDrawingTaskInStorage,
  untrackSummaryTaskInStorage,
  writeTaskNotificationSettings,
} from "@/lib/task-notification-state";

export const FINDIN_TASK_NOTIFICATION_EVENT = "findin:task-notification";
export type TaskNotificationStatus = "succeeded" | "failed";
export type {
  DesktopNotificationMode,
  PendingSummaryTask,
  SummaryTaskNotificationStage,
  TaskNotificationSettings,
};
export { defaultTaskNotificationSettings, createTaskNotificationDeduper };
export { resolveSummaryTaskNotificationOutcome };

export type TaskNotification = {
  taskType: "research-drawing" | "paper-upload" | "outline-generation" | "literature-review";
  taskId: string;
  status: TaskNotificationStatus;
  title: string;
  body: string;
  href?: string;
  error?: {
    code?: number;
    type?: string;
  };
};

export function getTaskNotificationSettings(): TaskNotificationSettings {
  return readTaskNotificationSettings(typeof window === "undefined" ? null : window.localStorage);
}

export function saveTaskNotificationSettings(settings: TaskNotificationSettings) {
  if (typeof window === "undefined") return;

  writeTaskNotificationSettings(window.localStorage, settings);
  window.dispatchEvent(new CustomEvent<TaskNotificationSettings>("findin:task-notification-settings", {
    detail: settings,
  }));
}

async function shouldSendDesktopNotification(mode: DesktopNotificationMode) {
  const desktopFocused = await getDesktopMainWindowFocused();
  return shouldDeliverDesktopNotification(mode, {
    desktopFocused,
    pageVisible: typeof document === "undefined" ? false : document.visibilityState === "visible",
    pageFocused: typeof document === "undefined" ? false : document.hasFocus(),
  });
}

function getPendingResearchDrawingTaskIds() {
  return readTrackedResearchDrawingTaskIds(typeof window === "undefined" ? null : window.sessionStorage);
}

export function trackResearchDrawingTask(taskId: number | string) {
  trackResearchDrawingTaskInStorage(typeof window === "undefined" ? null : window.sessionStorage, taskId);
}

export function getTrackedResearchDrawingTaskIds() {
  return getPendingResearchDrawingTaskIds();
}

export function untrackResearchDrawingTask(taskId: number | string) {
  untrackResearchDrawingTaskInStorage(typeof window === "undefined" ? null : window.sessionStorage, taskId);
}

export function getTrackedSummaryTasks(): PendingSummaryTask[] {
  return readTrackedSummaryTasks(typeof window === "undefined" ? null : window.sessionStorage);
}

export function trackSummaryTask(
  summaryId: number | string,
  stage: SummaryTaskNotificationStage,
  workflowId?: number | string,
) {
  trackSummaryTaskInStorage(
    typeof window === "undefined" ? null : window.sessionStorage,
    summaryId,
    stage,
    workflowId,
  );
}

export function untrackSummaryTask(summaryId: number | string, stage?: SummaryTaskNotificationStage) {
  untrackSummaryTaskInStorage(typeof window === "undefined" ? null : window.sessionStorage, summaryId, stage);
}

export async function publishTaskNotification(notification: TaskNotification) {
  if (typeof window === "undefined") {
    return false;
  }

  const settings = getTaskNotificationSettings();
  let inAppDelivered = false;
  if (shouldDeliverInAppNotification(settings)) {
    const event = new CustomEvent<TaskNotification>(FINDIN_TASK_NOTIFICATION_EVENT, {
      detail: notification,
      cancelable: true,
    });
    inAppDelivered = !window.dispatchEvent(event);
    if (!inAppDelivered) {
      console.warn("[Findin Task Notification] in-app notification had no active renderer", {
        taskType: notification.taskType,
        taskId: notification.taskId,
      });
    }
  }

  const desktopDelivered = await shouldSendDesktopNotification(settings.desktopNotificationMode)
    ? await sendDesktopNotification(notification.title, notification.body, notification.href)
    : false;
  console.info("[Findin Task Notification] terminal event handled", {
    taskType: notification.taskType,
    taskId: notification.taskId,
    status: notification.status,
    inAppDelivered,
    desktopDelivered,
  });
  return desktopDelivered;
}
